import React, { useMemo, useState } from 'react';
import { Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Tooltip,
  Legend,
} from 'chart.js';
import {
  KpiCard,
  SectionTitle,
  ChipStrip,
  FilterSelect,
  SearchField,
  InsightCard,
  PriorityBadge,
  EmptyState,
  PeriodUnavailableState,
  SubTabPanel,
  LoadMoreButton,
  DomainBadge,
  palette,
  chartBaseOptions,
  toneAccent,
  InsightBanner,
} from '../dashboardPrimitives';
import {
  CIRCLE_OPTIONS,
  MISC_PM_DOMAIN_TABS,
  MISC_PM_DOMAIN_GRADIENTS,
  MISC_PM_OSC_KPIS,
  MISC_PM_AG1_KPIS,
  MISC_PM_MFL_KPIS,
  MISC_PM_OSC,
  MISC_PM_AG1,
  MISC_PM_MFL,
  MISC_PM_INSIGHT_CHIPS,
  MISC_PM_INSIGHTS,
  MISC_PM_RECOVERABLE_BANNER,
  MISC_PM_ACTIONS,
} from '../../data/miscPmData';

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Tooltip, Legend);

/* ── Helpers ── */

function heatmapPillColor(pct) {
  if (pct >= 95) return { bg: '#DCFCE7', text: '#166534' };
  if (pct >= 80) return { bg: '#DBEAFE', text: '#1D4ED8' };
  if (pct >= 60) return { bg: '#FEF9C3', text: '#92400E' };
  if (pct >= 40) return { bg: '#FED7AA', text: '#9A3412' };
  return { bg: '#FEE2E2', text: '#991B1B' };
}

function HeatmapPill({ value, suffix = '%' }) {
  const pct = typeof value === 'number' ? value : 0;
  const tone = heatmapPillColor(pct);
  return (
    <span className="heatmap-pill" style={{ backgroundColor: tone.bg, color: tone.text }}>
      {pct.toFixed(1)}{suffix}
    </span>
  );
}

function ProgressBar({ pct }) {
  const color = pct >= 80 ? palette.success : pct >= 50 ? palette.warning : palette.danger;
  return (
    <div className="flex items-center gap-2">
      <div className="h-2 w-full min-w-[60px] overflow-hidden rounded-full bg-slate-100">
        <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(pct, 100)}%`, backgroundColor: color }} />
      </div>
      <span className="text-[10px] font-semibold text-slate-500" style={{ minWidth: 36 }}>{pct.toFixed(1)}%</span>
    </div>
  );
}

function filterByCircle(rows, circle) {
  if (circle === 'ALL') return rows;
  return rows.filter((r) => r.circle === circle);
}

function toneColor(t) {
  const map = { green: palette.success, red: palette.danger, orange: palette.warning, blue: palette.accent, teal: '#14B8A6', purple: '#8B5CF6' };
  return map[t] || palette.slate;
}

const PAGE_SIZE = 20;

/* ── OSC Section ── */

function OscSection({ circleFilter }) {
  const [oscTab, setOscTab] = useState(0);
  const [smpsSearch, setSmpsSearch] = useState('');
  const [smpsCmpFilter, setSmpsCmpFilter] = useState('ALL');
  const [smpsCircleFilter, setSmpsCircleFilter] = useState('ALL');
  const [smpsLimit, setSmpsLimit] = useState(PAGE_SIZE);
  const [noWoSearch, setNoWoSearch] = useState('');
  const [noWoCmpFilter, setNoWoCmpFilter] = useState('ALL');
  const [noWoCircleFilter, setNoWoCircleFilter] = useState('ALL');
  const [noWoLimit, setNoWoLimit] = useState(PAGE_SIZE);
  const [noFinalSearch, setNoFinalSearch] = useState('');
  const [noFinalCmpFilter, setNoFinalCmpFilter] = useState('ALL');
  const [noFinalCircleFilter, setNoFinalCircleFilter] = useState('ALL');
  const [noFinalLimit, setNoFinalLimit] = useState(PAGE_SIZE);

  const heatRows = useMemo(() => filterByCircle(MISC_PM_OSC.heatmap, circleFilter), [circleFilter]);

  const cmpOptions = useMemo(() => {
    const cmps = [...new Set(heatRows.map((r) => r.cmp))].sort();
    return [{ value: 'ALL', label: 'All CMPs' }, ...cmps.map((c) => ({ value: c, label: c }))];
  }, [heatRows]);

  const completionChartData = useMemo(() => {
    const sorted = [...heatRows].sort((a, b) => (a.final_pm / a.scope) * 100 - (b.final_pm / b.scope) * 100);
    return {
      labels: sorted.map((r) => r.cmp),
      datasets: [{
        label: 'Final PM %',
        data: sorted.map((r) => ((r.final_pm / r.scope) * 100).toFixed(1)),
        backgroundColor: sorted.map((r) => {
          const pct = (r.final_pm / r.scope) * 100;
          return pct >= 70 ? palette.success : pct >= 50 ? palette.warning : palette.danger;
        }),
        borderRadius: 4,
      }],
    };
  }, [heatRows]);

  const smpsChartData = useMemo(() => {
    const sorted = [...heatRows].sort((a, b) => b.smps_nr - a.smps_nr);
    return {
      labels: sorted.map((r) => r.cmp),
      datasets: [{
        label: 'SMPS Not Reachable',
        data: sorted.map((r) => r.smps_nr),
        backgroundColor: palette.danger,
        borderRadius: 4,
      }],
    };
  }, [heatRows]);

  const categoryDoughnutData = useMemo(() => ({
    labels: MISC_PM_OSC.charts.categoryDoughnut.labels,
    datasets: [{
      data: MISC_PM_OSC.charts.categoryDoughnut.data,
      backgroundColor: MISC_PM_OSC.charts.categoryDoughnut.colors,
      borderWidth: 0,
    }],
  }), []);

  const circleGroupedBarData = useMemo(() => {
    const circles = circleFilter === 'ALL'
      ? MISC_PM_OSC.charts.circleGroupedBar.circles
      : MISC_PM_OSC.charts.circleGroupedBar.circles.filter((c) => c.c === circleFilter);
    return {
      labels: circles.map((c) => c.c),
      datasets: [
        { label: 'Scope', data: circles.map((c) => c.scope), backgroundColor: palette.slate, borderRadius: 4 },
        { label: 'WO Raised', data: circles.map((c) => c.wo), backgroundColor: palette.accent, borderRadius: 4 },
        { label: 'Final PM', data: circles.map((c) => c.final_pm), backgroundColor: palette.success, borderRadius: 4 },
      ],
    };
  }, [circleFilter]);

  const filterDrilldown = (sites, search, cmpF, circleF) => {
    let rows = sites;
    if (circleFilter !== 'ALL') rows = rows.filter((s) => s.r === circleFilter);
    if (circleF !== 'ALL') rows = rows.filter((s) => s.r === circleF);
    if (cmpF !== 'ALL') rows = rows.filter((s) => s.c === cmpF);
    if (search.trim()) {
      const q = search.toLowerCase();
      rows = rows.filter((s) => s.s.toLowerCase().includes(q) || s.c.toLowerCase().includes(q) || (s.j && s.j.toLowerCase().includes(q)));
    }
    return rows;
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const smpsSites = useMemo(() => filterDrilldown(MISC_PM_OSC.drilldown.smpsSites, smpsSearch, smpsCmpFilter, smpsCircleFilter), [smpsSearch, smpsCmpFilter, smpsCircleFilter, circleFilter]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const noWoSites = useMemo(() => filterDrilldown(MISC_PM_OSC.drilldown.noWoSites, noWoSearch, noWoCmpFilter, noWoCircleFilter), [noWoSearch, noWoCmpFilter, noWoCircleFilter, circleFilter]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const noFinalSites = useMemo(() => filterDrilldown(MISC_PM_OSC.drilldown.noFinalSites, noFinalSearch, noFinalCmpFilter, noFinalCircleFilter), [noFinalSearch, noFinalCmpFilter, noFinalCircleFilter, circleFilter]);

  const oscDrilldownTabs = [
    { label: 'SMPS Not Reachable', count: smpsSites.length },
    { label: 'No WO Raised', count: noWoSites.length },
    { label: 'WO Done No Final PM', count: noFinalSites.length },
  ];

  const renderSiteTable = (sites, limit, setLimit, extraCol) => {
    if (!sites.length) return <EmptyState icon="search" className="min-h-[180px]" />;
    const visible = sites.slice(0, limit);
    return (
      <>
        <div className="table-scroll-shell">
          <table className="evidence-table">
            <thead>
              <tr>
                <th>#</th><th>Site ID</th><th>JC</th><th>CMP</th><th>Circle</th><th>Type</th>
                {extraCol ? <th>{extraCol}</th> : null}
              </tr>
            </thead>
            <tbody>
              {visible.map((s, i) => (
                <tr key={`${s.s}-${i}`}>
                  <td className="mono-cell text-slate-400">{i + 1}</td>
                  <td className="font-medium text-slate-700">{s.s}</td>
                  <td className="mono-cell">{s.j}</td>
                  <td>{s.c}</td>
                  <td><span className="inline-flex rounded-full px-2 py-1 text-[10px] font-semibold" style={{ backgroundColor: '#DBEAFE', color: '#1D4ED8' }}>{s.r}</span></td>
                  <td className="text-[11px] text-slate-500">{s.t}</td>
                  {extraCol && s.m !== undefined ? (
                    <td><span className={s.m === 'Yes' ? 'status-badge status-badge-red' : 'status-badge status-badge-green'}>{s.m === 'Yes' ? 'SMPS NR' : 'OK'}</span></td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {limit < sites.length ? <div className="mt-3"><LoadMoreButton remaining={sites.length - limit} onClick={() => setLimit((p) => p + PAGE_SIZE)} /></div> : null}
      </>
    );
  };

  const drilldownFilters = (search, setSearch, cmpF, setCmpF, circleF, setCircleF) => (
    <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
      <SearchField value={search} onChange={setSearch} placeholder="Search sites..." className="flex-1 sm:min-w-[200px]" />
      <FilterSelect label="CMP" value={cmpF} onChange={setCmpF} options={cmpOptions} className="sm:min-w-[150px]" />
      <FilterSelect label="Circle" value={circleF} onChange={setCircleF} options={CIRCLE_OPTIONS} className="sm:min-w-[150px]" />
    </div>
  );

  if (!heatRows.length) return <EmptyState />;

  const totalScope = heatRows.reduce((s, r) => s + r.scope, 0);

  return (
    <div className="space-y-6">
      {/* Domain Header */}
      <div className="domain-section-hdr" style={{ background: MISC_PM_DOMAIN_GRADIENTS.OSC }}>
        <h2>OSC - Outdoor Small Cells</h2>
        <span className="domain-section-chip">{heatRows.length} CMPs</span>
        <span className="domain-section-chip">{totalScope.toLocaleString()} Sites</span>
        <span className="domain-section-chip domain-section-chip-warn">48.9% Final PM</span>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {MISC_PM_OSC_KPIS.map((kpi) => (
          <KpiCard key={kpi.label} label={kpi.label} value={kpi.value} sub={kpi.sub} dot={toneColor(kpi.tone)} valueColor={toneColor(kpi.tone)} />
        ))}
      </div>

      {/* Charts 2x2 */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="card-surface p-6">
          <SectionTitle dot={palette.accent}>OSC PM Completion by CMP (%)</SectionTitle>
          <p className="dashboard-note">Sorted ascending. Green = 70%+, amber = 50-70%, red = below 50%.</p>
          <div style={{ height: 320 }}>
            <Bar data={completionChartData} options={{ ...chartBaseOptions, indexAxis: 'y', plugins: { ...chartBaseOptions.plugins, legend: { display: false }, tooltip: { ...chartBaseOptions.plugins.tooltip, callbacks: { label: (ctx) => ` ${Number(ctx.raw).toFixed(1)}%` } } }, scales: { x: { min: 0, max: 100, grid: { color: palette.border }, border: { display: false }, ticks: { callback: (v) => `${v}%`, color: palette.slateDeep, font: { family: 'JetBrains Mono', size: 10, weight: '500' } } }, y: { grid: { display: false }, border: { display: false }, ticks: { color: palette.slateDeep, font: { family: 'Inter', size: 10, weight: '500' } } } } }} />
          </div>
        </div>
        <div className="card-surface p-6">
          <SectionTitle dot={palette.danger}>SMPS Not Reachable by CMP</SectionTitle>
          <p className="dashboard-note">Sites where SMPS is flagged not reachable, blocking field PM execution.</p>
          <div style={{ height: 320 }}>
            <Bar data={smpsChartData} options={{ ...chartBaseOptions, plugins: { ...chartBaseOptions.plugins, legend: { display: false }, tooltip: { ...chartBaseOptions.plugins.tooltip, callbacks: { label: (ctx) => ` ${ctx.raw} sites` } } }, scales: { x: { grid: { display: false }, border: { display: false }, ticks: { color: palette.slateDeep, font: { family: 'Inter', size: 10, weight: '500' } } }, y: { grid: { color: palette.border }, border: { display: false }, ticks: { color: palette.slateDeep, font: { family: 'JetBrains Mono', size: 10, weight: '500' } } } } }} />
          </div>
        </div>
        <div className="card-surface p-6">
          <SectionTitle dot="#8B5CF6">OSC Category Breakdown</SectionTitle>
          <p className="dashboard-note">{MISC_PM_OSC.scope.totalSites.toLocaleString()} total OSC sites across 4 equipment categories.</p>
          <div style={{ height: 280 }} className="flex items-center justify-center">
            <Doughnut data={categoryDoughnutData} options={{ ...chartBaseOptions, cutout: '55%', plugins: { ...chartBaseOptions.plugins, legend: { display: true, position: 'right', labels: { color: palette.slateDeep, font: { family: 'Inter', size: 10, weight: '500' }, usePointStyle: true, pointStyle: 'circle', boxWidth: 8, boxHeight: 8, padding: 14 } }, tooltip: { ...chartBaseOptions.plugins.tooltip, callbacks: { label: (ctx) => ` ${ctx.label}: ${ctx.raw.toLocaleString()} sites` } } } }} />
          </div>
        </div>
        <div className="card-surface p-6">
          <SectionTitle dot={palette.navy}>OSC by Circle - Scope / WO / Final PM</SectionTitle>
          <p className="dashboard-note">Grouped bar showing pipeline from scope to WO to Final PM by circle.</p>
          <div style={{ height: 280 }}>
            <Bar data={circleGroupedBarData} options={{ ...chartBaseOptions, plugins: { ...chartBaseOptions.plugins, legend: { display: true, position: 'top', labels: { color: palette.slateDeep, font: { family: 'Inter', size: 10, weight: '500' } } }, tooltip: { ...chartBaseOptions.plugins.tooltip, callbacks: { label: (ctx) => ` ${ctx.dataset.label}: ${ctx.raw.toLocaleString()} sites` } } }, scales: { x: { grid: { display: false }, border: { display: false }, ticks: { color: palette.slateDeep, font: { family: 'Inter', size: 10, weight: '500' } } }, y: { grid: { color: palette.border }, border: { display: false }, ticks: { color: palette.slateDeep, font: { family: 'JetBrains Mono', size: 10, weight: '500' } } } } }} />
          </div>
        </div>
      </div>

      {/* Heatmap Table */}
      <div className="card-surface p-4">
        <SectionTitle dot="#059669">OSC PM Execution Heatmap</SectionTitle>
        <div className="table-scroll-shell">
          <table className="evidence-table">
            <thead>
              <tr>
                <th>CMP</th><th>Circle</th><th>Scope</th><th>WO Raised</th><th>Final PM</th><th>Completion %</th><th>SMPS NR</th><th>SMPS % of Scope</th><th>Progress</th>
              </tr>
            </thead>
            <tbody>
              {heatRows.map((r) => {
                const pct = (r.final_pm / r.scope) * 100;
                const smpsPct = (r.smps_nr / r.scope) * 100;
                return (
                  <tr key={r.cmp}>
                    <td className="font-semibold text-slate-700">{r.cmp}</td>
                    <td><span className="inline-flex rounded-full px-2 py-1 text-[10px] font-semibold" style={{ backgroundColor: '#DBEAFE', color: '#1D4ED8' }}>{r.circle}</span></td>
                    <td className="mono-cell">{r.scope.toLocaleString()}</td>
                    <td className="mono-cell">{r.wo.toLocaleString()}</td>
                    <td className="mono-cell">{r.final_pm.toLocaleString()}</td>
                    <td><HeatmapPill value={pct} /></td>
                    <td className="mono-cell font-semibold text-red-600">{r.smps_nr.toLocaleString()}</td>
                    <td><HeatmapPill value={smpsPct} /></td>
                    <td style={{ minWidth: 120 }}><ProgressBar pct={pct} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Drilldown */}
      <SubTabPanel tabs={oscDrilldownTabs} activeTab={oscTab} onTabChange={(i) => { setOscTab(i); setSmpsLimit(PAGE_SIZE); setNoWoLimit(PAGE_SIZE); setNoFinalLimit(PAGE_SIZE); }}>
        {oscTab === 0 && <>{drilldownFilters(smpsSearch, setSmpsSearch, smpsCmpFilter, setSmpsCmpFilter, smpsCircleFilter, setSmpsCircleFilter)}{renderSiteTable(smpsSites, smpsLimit, setSmpsLimit)}</>}
        {oscTab === 1 && <>{drilldownFilters(noWoSearch, setNoWoSearch, noWoCmpFilter, setNoWoCmpFilter, noWoCircleFilter, setNoWoCircleFilter)}{renderSiteTable(noWoSites, noWoLimit, setNoWoLimit)}</>}
        {oscTab === 2 && <>{drilldownFilters(noFinalSearch, setNoFinalSearch, noFinalCmpFilter, setNoFinalCmpFilter, noFinalCircleFilter, setNoFinalCircleFilter)}{renderSiteTable(noFinalSites, noFinalLimit, setNoFinalLimit, 'SMPS')}</>}
      </SubTabPanel>
    </div>
  );
}

/* ── AG1 Section ── */

function Ag1Section({ circleFilter }) {
  const [ag1Tab, setAg1Tab] = useState(0);
  const [ag1Search, setAg1Search] = useState('');
  const [ag1CmpFilter, setAg1CmpFilter] = useState('ALL');
  const [ag1Limit, setAg1Limit] = useState(PAGE_SIZE);

  const heatRows = useMemo(() => filterByCircle(MISC_PM_AG1.heatmap, circleFilter), [circleFilter]);

  const cmpOptions = useMemo(() => {
    const cmps = [...new Set(heatRows.map((r) => r.cmp))].sort();
    return [{ value: 'ALL', label: 'All CMPs' }, ...cmps.map((c) => ({ value: c, label: c }))];
  }, [heatRows]);

  const ag1ChartData = useMemo(() => {
    const sorted = [...heatRows].sort((a, b) => {
      const aPct = a.scope > 0 ? (a.teco / a.scope) * 100 : 0;
      const bPct = b.scope > 0 ? (b.teco / b.scope) * 100 : 0;
      return aPct - bPct;
    });
    return {
      labels: sorted.map((r) => r.cmp),
      datasets: [
        { label: 'TECO Done', data: sorted.map((r) => r.teco), backgroundColor: palette.success, borderRadius: 4 },
        { label: 'WOCL Pending TECO', data: sorted.map((r) => r.wocl - r.teco), backgroundColor: palette.warning, borderRadius: 4 },
        { label: 'WO Not Created', data: sorted.map((r) => r.no_wo), backgroundColor: palette.danger, borderRadius: 4 },
      ],
    };
  }, [heatRows]);

  const allAg1Sites = useMemo(() => {
    let rows = MISC_PM_AG1.drilldown.allSites;
    if (circleFilter !== 'ALL') rows = rows.filter((s) => s.r === circleFilter);
    return rows;
  }, [circleFilter]);

  const filteredAg1Sites = useMemo(() => {
    let sites;
    if (ag1Tab === 0) sites = allAg1Sites;
    else if (ag1Tab === 1) sites = allAg1Sites.filter((s) => s.wo === 0);
    else sites = allAg1Sites.filter((s) => s.teco === 1);
    if (ag1CmpFilter !== 'ALL') sites = sites.filter((s) => s.c === ag1CmpFilter);
    if (ag1Search.trim()) {
      const q = ag1Search.toLowerCase();
      sites = sites.filter((s) => s.f.toLowerCase().includes(q) || s.s.toLowerCase().includes(q) || s.c.toLowerCase().includes(q));
    }
    return sites;
  }, [ag1Tab, allAg1Sites, ag1CmpFilter, ag1Search]);

  const ag1DrillTabs = [
    { label: 'All Sites', count: allAg1Sites.length },
    { label: 'WO Not Created', count: allAg1Sites.filter((s) => s.wo === 0).length },
    { label: 'TECO Done', count: allAg1Sites.filter((s) => s.teco === 1).length },
  ];

  if (!heatRows.length) return <EmptyState />;

  const totalScope = heatRows.reduce((s, r) => s + r.scope, 0);

  return (
    <div className="space-y-6">
      {/* Domain Header */}
      <div className="domain-section-hdr" style={{ background: MISC_PM_DOMAIN_GRADIENTS.AG1 }}>
        <h2>AG1 - Aggregator Nodes Level 1</h2>
        <span className="domain-section-chip">{heatRows.length} CMPs</span>
        <span className="domain-section-chip">{totalScope} Sites</span>
        <span className="domain-section-chip domain-section-chip-warn">53.2% TECO Done</span>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {MISC_PM_AG1_KPIS.map((kpi) => (
          <KpiCard key={kpi.label} label={kpi.label} value={kpi.value} sub={kpi.sub} dot={toneColor(kpi.tone)} valueColor={toneColor(kpi.tone)} />
        ))}
      </div>

      {/* Chart */}
      <div className="card-surface p-6">
        <SectionTitle dot="#8B5CF6">AG1 Status by CMP</SectionTitle>
        <p className="dashboard-note">Stacked bar: TECO Done / WOCL Pending / No WO. Sorted by TECO %.</p>
        <div style={{ height: 300 }}>
          <Bar data={ag1ChartData} options={{ ...chartBaseOptions, indexAxis: 'y', plugins: { ...chartBaseOptions.plugins, legend: { display: true, position: 'top', labels: { color: palette.slateDeep, font: { family: 'Inter', size: 10, weight: '500' } } }, tooltip: { ...chartBaseOptions.plugins.tooltip, callbacks: { label: (ctx) => ` ${ctx.dataset.label}: ${ctx.raw} sites` } } }, scales: { x: { stacked: true, grid: { color: palette.border }, border: { display: false }, ticks: { color: palette.slateDeep, font: { family: 'JetBrains Mono', size: 10, weight: '500' } } }, y: { stacked: true, grid: { display: false }, border: { display: false }, ticks: { color: palette.slateDeep, font: { family: 'Inter', size: 10, weight: '500' } } } } }} />
        </div>
      </div>

      {/* Heatmap Table */}
      <div className="card-surface p-4">
        <SectionTitle dot="#ec4899">AG1 PM Execution Heatmap</SectionTitle>
        <div className="table-scroll-shell">
          <table className="evidence-table">
            <thead>
              <tr>
                <th>CMP</th><th>Circle</th><th>Scope</th><th>WO Created</th><th>WOCL Done</th><th>TECO Done</th><th>TECO %</th><th>No WO</th><th>Progress</th>
              </tr>
            </thead>
            <tbody>
              {heatRows.map((r) => {
                const tecoPct = r.scope > 0 ? (r.teco / r.scope) * 100 : 0;
                return (
                  <tr key={r.cmp} className={r.no_wo > 0 ? 'bg-red-50/60' : ''}>
                    <td className="font-semibold text-slate-700">{r.cmp}</td>
                    <td><span className="inline-flex rounded-full px-2 py-1 text-[10px] font-semibold" style={{ backgroundColor: '#EDE9FE', color: '#5B21B6' }}>{r.circle}</span></td>
                    <td className="mono-cell">{r.scope}</td>
                    <td className="mono-cell">{r.wo}</td>
                    <td className="mono-cell">{r.wocl}</td>
                    <td className="mono-cell">{r.teco}</td>
                    <td><HeatmapPill value={tecoPct} /></td>
                    <td className={`mono-cell font-semibold ${r.no_wo > 0 ? 'text-red-600' : 'text-slate-400'}`}>{r.no_wo > 0 ? r.no_wo : '-'}</td>
                    <td style={{ minWidth: 120 }}><ProgressBar pct={tecoPct} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Drilldown */}
      <SubTabPanel tabs={ag1DrillTabs} activeTab={ag1Tab} onTabChange={(i) => { setAg1Tab(i); setAg1Limit(PAGE_SIZE); }}>
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
          <SearchField value={ag1Search} onChange={setAg1Search} placeholder="Search FAC / Site..." className="flex-1 sm:min-w-[200px]" />
          <FilterSelect label="CMP" value={ag1CmpFilter} onChange={setAg1CmpFilter} options={cmpOptions} className="sm:min-w-[150px]" />
        </div>
        {filteredAg1Sites.length ? (
          <>
            <div className="table-scroll-shell">
              <table className="evidence-table">
                <thead>
                  <tr><th>#</th><th>FAC ID</th><th>Site ID</th><th>CMP</th><th>Circle</th><th>WO</th><th>WOCL</th><th>TECO</th></tr>
                </thead>
                <tbody>
                  {filteredAg1Sites.slice(0, ag1Limit).map((s, i) => (
                    <tr key={`${s.f}-${i}`} className={s.wo === 0 ? 'bg-red-50/60' : ''}>
                      <td className="mono-cell text-slate-400">{i + 1}</td>
                      <td className="font-medium text-slate-700 text-[11px]">{s.f}</td>
                      <td className="mono-cell text-[11px]">{s.s}</td>
                      <td>{s.c}</td>
                      <td><span className="inline-flex rounded-full px-2 py-1 text-[10px] font-semibold" style={{ backgroundColor: '#EDE9FE', color: '#5B21B6' }}>{s.r}</span></td>
                      <td><span className={s.wo ? 'status-badge status-badge-green' : 'status-badge status-badge-red'}>{s.wo ? 'Yes' : 'No'}</span></td>
                      <td><span className={s.wocl ? 'status-badge status-badge-green' : 'status-badge status-badge-red'}>{s.wocl ? 'Done' : 'No'}</span></td>
                      <td><span className={s.teco ? 'status-badge status-badge-green' : 'status-badge status-badge-red'}>{s.teco ? 'Done' : 'No'}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {ag1Limit < filteredAg1Sites.length ? <div className="mt-3"><LoadMoreButton remaining={filteredAg1Sites.length - ag1Limit} onClick={() => setAg1Limit((p) => p + PAGE_SIZE)} /></div> : null}
          </>
        ) : <EmptyState icon="search" className="min-h-[180px]" />}
      </SubTabPanel>
    </div>
  );
}

/* ── MFL Section ── */

function MflSection({ circleFilter }) {
  const [mflTab, setMflTab] = useState(0);
  const [mflSearch, setMflSearch] = useState('');
  const [mflCmpFilter, setMflCmpFilter] = useState('ALL');
  const [mflLimit, setMflLimit] = useState(PAGE_SIZE);

  const heatRows = useMemo(() => filterByCircle(MISC_PM_MFL.heatmap, circleFilter), [circleFilter]);

  const cmpOptions = useMemo(() => {
    const cmps = [...new Set(heatRows.map((r) => r.cmp))].sort();
    return [{ value: 'ALL', label: 'All CMPs' }, ...cmps.map((c) => ({ value: c, label: c }))];
  }, [heatRows]);

  const tecoChartData = useMemo(() => {
    const sorted = [...heatRows].sort((a, b) => {
      const aPct = a.scope > 0 ? (a.teco / a.scope) * 100 : 0;
      const bPct = b.scope > 0 ? (b.teco / b.scope) * 100 : 0;
      return aPct - bPct;
    });
    return {
      labels: sorted.map((r) => r.cmp),
      datasets: [{
        label: 'TECO %',
        data: sorted.map((r) => ((r.teco / r.scope) * 100).toFixed(1)),
        backgroundColor: sorted.map((r) => {
          const pct = (r.teco / r.scope) * 100;
          return pct >= 95 ? palette.success : pct >= 80 ? palette.warning : palette.danger;
        }),
        borderRadius: 4,
      }],
    };
  }, [heatRows]);

  const facilityDoughnutData = useMemo(() => ({
    labels: MISC_PM_MFL.charts.facilityTypeDoughnut.labels,
    datasets: [{
      data: MISC_PM_MFL.charts.facilityTypeDoughnut.data,
      backgroundColor: MISC_PM_MFL.charts.facilityTypeDoughnut.colors,
      borderWidth: 0,
    }],
  }), []);

  const allMflSites = useMemo(() => {
    let rows = MISC_PM_MFL.drilldown.allSites;
    if (circleFilter !== 'ALL') rows = rows.filter((s) => s.r === circleFilter);
    return rows;
  }, [circleFilter]);

  const filteredMflSites = useMemo(() => {
    let sites;
    if (mflTab === 0) sites = allMflSites;
    else if (mflTab === 1) sites = allMflSites.filter((s) => s.teco !== 'Done');
    else sites = allMflSites.filter((s) => s.teco === 'Done');
    if (mflCmpFilter !== 'ALL') sites = sites.filter((s) => s.c === mflCmpFilter);
    if (mflSearch.trim()) {
      const q = mflSearch.toLowerCase();
      sites = sites.filter((s) => s.f.toLowerCase().includes(q) || s.s.toLowerCase().includes(q) || s.c.toLowerCase().includes(q) || (s.n && s.n.toLowerCase().includes(q)));
    }
    return sites;
  }, [mflTab, allMflSites, mflCmpFilter, mflSearch]);

  const mflDrillTabs = [
    { label: 'All Sites', count: allMflSites.length },
    { label: 'Pending TECO', count: allMflSites.filter((s) => s.teco !== 'Done').length },
    { label: 'TECO Done', count: allMflSites.filter((s) => s.teco === 'Done').length },
  ];

  if (!heatRows.length) return <EmptyState />;

  const totalScope = heatRows.reduce((s, r) => s + r.scope, 0);

  return (
    <div className="space-y-6">
      {/* Domain Header */}
      <div className="domain-section-hdr" style={{ background: MISC_PM_DOMAIN_GRADIENTS.MFL }}>
        <h2>MFL - Main Feeder Links</h2>
        <span className="domain-section-chip">{heatRows.length} CMPs</span>
        <span className="domain-section-chip">{totalScope} Sites</span>
        <span className="domain-section-chip">96.4% TECO Done</span>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {MISC_PM_MFL_KPIS.map((kpi) => (
          <KpiCard key={kpi.label} label={kpi.label} value={kpi.value} sub={kpi.sub} dot={toneColor(kpi.tone)} valueColor={toneColor(kpi.tone)} />
        ))}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="card-surface p-6">
          <SectionTitle dot="#14B8A6">MFL TECO Rate by CMP (%)</SectionTitle>
          <p className="dashboard-note">Sorted ascending. Green = 95%+, Amber = 80-95%, Red = below 80%.</p>
          <div style={{ height: 320 }}>
            <Bar data={tecoChartData} options={{ ...chartBaseOptions, indexAxis: 'y', plugins: { ...chartBaseOptions.plugins, legend: { display: false }, tooltip: { ...chartBaseOptions.plugins.tooltip, callbacks: { label: (ctx) => ` ${Number(ctx.raw).toFixed(1)}%` } } }, scales: { x: { min: 0, max: 100, grid: { color: palette.border }, border: { display: false }, ticks: { callback: (v) => `${v}%`, color: palette.slateDeep, font: { family: 'JetBrains Mono', size: 10, weight: '500' } } }, y: { grid: { display: false }, border: { display: false }, ticks: { color: palette.slateDeep, font: { family: 'Inter', size: 10, weight: '500' } } } } }} />
          </div>
        </div>
        <div className="card-surface p-6">
          <SectionTitle dot="#14B8A6">MFL Facility Type Breakdown</SectionTitle>
          <p className="dashboard-note">{MISC_PM_MFL.scope.totalSites} total MFL sites across 5 facility types.</p>
          <div style={{ height: 280 }} className="flex items-center justify-center">
            <Doughnut data={facilityDoughnutData} options={{ ...chartBaseOptions, cutout: '55%', plugins: { ...chartBaseOptions.plugins, legend: { display: true, position: 'right', labels: { color: palette.slateDeep, font: { family: 'Inter', size: 10, weight: '500' }, usePointStyle: true, pointStyle: 'circle', boxWidth: 8, boxHeight: 8, padding: 14 } }, tooltip: { ...chartBaseOptions.plugins.tooltip, callbacks: { label: (ctx) => ` ${ctx.label}: ${ctx.raw} sites` } } } }} />
          </div>
        </div>
      </div>

      {/* Heatmap Table */}
      <div className="card-surface p-4">
        <SectionTitle dot="#3b82f6">MFL PM Execution Heatmap</SectionTitle>
        <div className="table-scroll-shell">
          <table className="evidence-table">
            <thead>
              <tr>
                <th>CMP</th><th>Circle</th><th>Scope</th><th>WO Raised</th><th>WOCL Done</th><th>TECO Done</th><th>TECO %</th><th>Pending</th><th>Progress</th>
              </tr>
            </thead>
            <tbody>
              {heatRows.map((r) => {
                const tecoPct = r.scope > 0 ? (r.teco / r.scope) * 100 : 0;
                const pending = r.scope - r.teco;
                return (
                  <tr key={r.cmp}>
                    <td className="font-semibold text-slate-700">{r.cmp}</td>
                    <td><span className="inline-flex rounded-full px-2 py-1 text-[10px] font-semibold" style={{ backgroundColor: '#CCFBF1', color: '#0D9488' }}>{r.circle}</span></td>
                    <td className="mono-cell">{r.scope}</td>
                    <td className="mono-cell">{r.wo}</td>
                    <td className="mono-cell">{r.wocl}</td>
                    <td className="mono-cell">{r.teco}</td>
                    <td><HeatmapPill value={tecoPct} /></td>
                    <td className={`mono-cell font-semibold ${pending > 0 ? 'text-amber-600' : 'text-slate-400'}`}>{pending > 0 ? pending : '-'}</td>
                    <td style={{ minWidth: 120 }}><ProgressBar pct={tecoPct} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Drilldown */}
      <SubTabPanel tabs={mflDrillTabs} activeTab={mflTab} onTabChange={(i) => { setMflTab(i); setMflLimit(PAGE_SIZE); }}>
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
          <SearchField value={mflSearch} onChange={setMflSearch} placeholder="Search FAC / Site / Name..." className="flex-1 sm:min-w-[200px]" />
          <FilterSelect label="CMP" value={mflCmpFilter} onChange={setMflCmpFilter} options={cmpOptions} className="sm:min-w-[150px]" />
        </div>
        {filteredMflSites.length ? (
          <>
            <div className="table-scroll-shell">
              <table className="evidence-table">
                <thead>
                  <tr><th>#</th><th>FAC ID</th><th>Site ID</th><th>Name</th><th>CMP</th><th>Circle</th><th>Facility Type</th><th>WOCL</th><th>TECO</th></tr>
                </thead>
                <tbody>
                  {filteredMflSites.slice(0, mflLimit).map((s, i) => (
                    <tr key={`${s.f}-${i}`} className={s.teco !== 'Done' ? 'bg-amber-50/60' : ''}>
                      <td className="mono-cell text-slate-400">{i + 1}</td>
                      <td className="font-medium text-slate-700 text-[11px]">{s.f}</td>
                      <td className="mono-cell text-[11px]">{s.s}</td>
                      <td className="max-w-[200px] truncate text-[11px] text-slate-600">{s.n}</td>
                      <td>{s.c}</td>
                      <td><span className="inline-flex rounded-full px-2 py-1 text-[10px] font-semibold" style={{ backgroundColor: '#CCFBF1', color: '#0D9488' }}>{s.r}</span></td>
                      <td className="text-[11px] text-slate-500">{s.ft}</td>
                      <td><span className={s.wocl === 'Done' ? 'status-badge status-badge-green' : s.wocl === 'Not Done' ? 'status-badge status-badge-red' : 'status-badge status-badge-slate'}>{s.wocl}</span></td>
                      <td><span className={s.teco === 'Done' ? 'status-badge status-badge-green' : 'status-badge status-badge-red'}>{s.teco}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {mflLimit < filteredMflSites.length ? <div className="mt-3"><LoadMoreButton remaining={filteredMflSites.length - mflLimit} onClick={() => setMflLimit((p) => p + PAGE_SIZE)} /></div> : null}
          </>
        ) : <EmptyState icon="search" className="min-h-[180px]" />}
      </SubTabPanel>
    </div>
  );
}

/* ── Main Component ── */

export default function MiscPM({ selectedMonth = "Feb'26" }) {
  const [activeDomain, setActiveDomain] = useState('MFL');
  const [circleFilter, setCircleFilter] = useState('ALL');

  if (selectedMonth !== "Feb'26") {
    return <PeriodUnavailableState monthLabel={selectedMonth} />;
  }

  return (
    <div className="space-y-6">
      {/* 1. Filter Strip */}
      <ChipStrip groups={[
        { label: 'Circle', value: circleFilter, onChange: setCircleFilter, options: CIRCLE_OPTIONS },
        { label: 'Type', value: activeDomain, onChange: setActiveDomain, options: MISC_PM_DOMAIN_TABS.map((t) => ({ value: t.key, label: t.label })) },
      ]} />

      {/* 4. Domain-Specific Content */}
      {activeDomain === 'MFL' && <MflSection circleFilter={circleFilter} />}
      {activeDomain === 'AG1' && <Ag1Section circleFilter={circleFilter} />}
      {activeDomain === 'OSC' && <OscSection circleFilter={circleFilter} />}

      {/* 5. Executive Insights */}
      <InsightBanner chips={MISC_PM_INSIGHT_CHIPS} />

      <div className="grid grid-cols-1 gap-4">
        {MISC_PM_INSIGHTS.map((insight) => (
          <InsightCard
            key={insight.title}
            tone={insight.tone}
            title={insight.title}
            exposure={insight.exposure}
            body={insight.body}
            actionTitle={insight.actionTitle || 'Recommended Action'}
            action={insight.action}
          >
            {insight.metrics?.length ? (
              <div className="mt-3 flex flex-wrap gap-3">
                {insight.metrics.map((m) => {
                  const accent = toneAccent(m.color);
                  return (
                    <div key={m.label} className="rounded-lg px-3 py-2 text-center" style={{ backgroundColor: accent.soft }}>
                      <div className="text-[14px] font-bold" style={{ color: accent.line }}>{m.value}</div>
                      <div className="text-[10px] font-medium text-slate-500">{m.label}</div>
                    </div>
                  );
                })}
              </div>
            ) : null}
          </InsightCard>
        ))}
      </div>

      {/* 6. Recoverable Banner + Action Plan */}
      {MISC_PM_ACTIONS?.length ? (
        <>
          <div className="card-surface border border-emerald-200 bg-emerald-50/80 p-6">
            <div className="text-[10px] font-semibold uppercase tracking-[0.06em] text-emerald-700">Recoverable Upside</div>
            <div className="mt-2 text-[13px] font-semibold leading-6 text-emerald-900">{MISC_PM_RECOVERABLE_BANNER}</div>
          </div>

          <div className="card-surface p-6">
            <SectionTitle dot={palette.success}>Prioritised Action Plan</SectionTitle>
            <div className="table-scroll-shell">
              <table className="action-table">
                <thead>
                  <tr>
                    <th>Priority</th><th>Domain</th><th>Action</th><th>CMPs Impacted</th><th>Owner</th><th>Expected Impact</th>
                  </tr>
                </thead>
                <tbody>
                  {MISC_PM_ACTIONS.map((row) => (
                    <tr key={row.action.slice(0, 60)}>
                      <td><PriorityBadge priority={row.priority} /></td>
                      <td><DomainBadge domain={row.domain} /></td>
                      <td>
                        <div className="font-semibold text-slate-700">{row.action.split(' \u2014 ')[0]}</div>
                        {row.action.includes(' \u2014 ') ? (
                          <div className="mt-1 text-[11px] leading-5 text-slate-500">{row.action.split(' \u2014 ').slice(1).join(' \u2014 ')}</div>
                        ) : null}
                        {row.pmImpact ? (
                          <div className="mt-1 text-[11px] leading-5 text-amber-700">PM Impact: {row.pmImpact}</div>
                        ) : null}
                      </td>
                      <td className="text-[11px] leading-5 text-slate-600">{row.cmpsAffected}</td>
                      <td className="text-slate-600">{row.owner}</td>
                      <td className="text-[11px] leading-5 text-slate-600">{row.expectedImpact}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}
